import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, map, Observable, of, tap } from 'rxjs';
import { BASE_URL } from '../app/token';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PortalService {

  private contentAttestationSubject = new BehaviorSubject<any[]>([]);
  contentAttestation$ = this.contentAttestationSubject.asObservable();
  private statesSubject = new BehaviorSubject<any[]>([]);
  states$ = this.statesSubject.asObservable();
  loadEnrollmentSteps() {
    throw new Error('Method not implemented.');
  }
  constructor(private httpClient: HttpClient, @Inject(BASE_URL) private baseUrl: string) { }


  uploadDocumentAttachment1(file: File, enrollmentId: string): Observable<any> {
    const apiUrl = `${this.baseUrl}/uploaddocumentattachment`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('enrollmentId', enrollmentId);
    return this.httpClient.post(apiUrl, formData);
  }


  uploadDocumentAttachment(formData: FormData): Observable<any> {
    const apiUrl = `${this.baseUrl}/uploaddocumentattachment`;
    return this.httpClient.post(apiUrl, formData);
  }

  missingDocumentAttachment(formData: FormData): Observable<any> {
    const apiUrl = `${this.baseUrl}/missingattachmentupload`;
    return this.httpClient.post(apiUrl, formData);
  }

  getEnrollmentAttachment(enrollmentId: string): Observable<any> {
    const apiUrl = `${this.baseUrl}/getenrollmentattachment/${enrollmentId}`;
    return this.httpClient.get(apiUrl);
  }

  removeDocumentAttachment(documentAttachmentId: number): Observable<any> {
    const apiUrl = `${this.baseUrl}/removedocumentattachment?documentAttachmentId=${documentAttachmentId}`;

    return this.httpClient.post(apiUrl, {});
  }

  submitEnrollment(enrollmentData: any, enrollmentId: string): Observable<any> {
    const apiUrl = `${this.baseUrl}/submitenrollment`;
    const params = new HttpParams().set('enrollmentId', enrollmentId);
    return this.httpClient.post(apiUrl, enrollmentData, { params });
  }

  getEnrollmentById(programId: string, enrollmentId: string): Observable<any> {
    const apiUrl = `${this.baseUrl}/enrollmentdetails`;
    const params = new HttpParams().set('enrollmentId', enrollmentId);
    return this.httpClient.get<any>(apiUrl, { params }).pipe(
      map(response => response) // Only return `enrollmentEligibilityViewDTOs`
    );
  }

  getEnrollmentSteps(programId: string): Observable<any[]> {
    const apiUrl = `${this.baseUrl}/enrollmentstepworkflow`;
    const params = { programId };
    return this.httpClient.get<any[]>(apiUrl, { params });
  }

  validateAddress(addressData: any): Observable<any> {
    const apiUrl = `${this.baseUrl}/address/validate`;    
    return this.httpClient.post(apiUrl, addressData);
  }

  getManufacturerProgram(programURL: string) {
    return this.httpClient.get(`${this.baseUrl}/manufacturerprogram`, {
      params: { programURL: programURL },
      headers: { 'api-supported-versions': '2.0' },
    });
  }

  getManufacturerProgramForGuard(programURL: string) {
    return this.httpClient.get(`${this.baseUrl}/manufacturerprogram`, {
      params: { programURL: programURL },
      headers: { 'api-supported-versions': '2.0' },
    });
  }

  getPatientAccountDetail(email: string) {
    return this.httpClient.get(`${this.baseUrl}/patientaccountdetails`, {
      params: { email: email },
      headers: { 'api-supported-versions': '2.0' },
    });
  }

  getPatientSubmissionsByPatientId(patientId: any) {
    return this.httpClient.get(`${this.baseUrl}/patientsubmissiondetailsbypatientid`, {
      params: { PatientAccountId: patientId },
      headers: { 'api-supported-versions': '2.0' },
    });
  }

  updatePatientProfile(profileData: any, patientAccountID: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'api-supported-versions': '2.0',
    });
    return this.httpClient.put(`${this.baseUrl}/updatepatientaccountdetails`, profileData, {
      headers,
      params: { patientAccountId: patientAccountID },
    });
  }

  createPatientProfile(profileData: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'api-supported-versions': '2.0',
    });
    return this.httpClient.post(`${this.baseUrl}/createpatientaccountdetails`, profileData, { headers });
  }

  getDrugList(): Observable<any> {
    return this.httpClient.get(`${this.baseUrl}/drug`, {
      headers: {
        'api-supported-versions': '2.0'
      }
    });
  }

  getStatesList(): Observable<any[]> {
    return this.httpClient.get<any[]>(`${this.baseUrl}/states/${environment.programID}`, {
      headers: {
        'api-supported-versions': '2.0'
      }
    }).pipe(
      tap(data => this.statesSubject.next(data))
    );
  }

  getContentAttestionConsentType() {
    this.httpClient.get<any[]>(`${this.baseUrl}/contentattestionconsenttype`, {
      headers: {
        'api-supported-versions': '2.0'
      }
    })
      .pipe(
        tap(data => this.contentAttestationSubject.next(data))
      )
      .subscribe();
  }

  getDataByConsentType(type: string): Observable<any[]> {
    return this.contentAttestation$.pipe(
      map(data => data.filter(item => item.contentAttestionConsentType === type))
    );
  }


  getEnrollmentStepWorkflow(programId: any) {
    return this.httpClient.get(`${this.baseUrl}/enrollmentstepworkflow`, {
      params: {
        programId: programId
      },
      headers: {
        'api-supported-versions': '2.0'
      }
    });
  }

  createEnrollment(EnrollmentForm: any, enrollmentID: any) {
    return this.httpClient.post(`${this.baseUrl}/createenrollment`, EnrollmentForm, {
      params: {
        enrollmentID: enrollmentID
      },
      headers: {
        'api-supported-versions': '2.0',
      }
    })
  }

  getProgramInsuranceType(programId: any) {
    return this.httpClient.get(`${this.baseUrl}/program-insurancetype/` + programId, {
      headers: {
        'api-supported-versions': '2.0'
      }
    });
  }

  financialMedicalHardshipType() {
    return this.httpClient.get(`${this.baseUrl}/financialmedicalhardshiptype`, {
      headers: {
        'api-supported-versions': '2.0'
      }
    });
  }
} 
